/* glose.js — Glosé interactive layer
 * Covers: cart drawer, ATC, quick-add, PDP options/qty, countdown, cart count sync
 */

(() => {
  'use strict';

  /* ── Helpers ─────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const dispatch = (name, detail) =>
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  /* Musí sedět s money formatem v Shopify adminu: €{{amount_no_decimals_with_comma_separator}} */
  const moneyNumber = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
  const formatMoney = cents => `€${moneyNumber.format(cents / 100)}`;

  /* ── Cart state ──────────────────────────────── */
  let cartState = null;

  async function fetchCart() {
    const r = await fetch('/cart.js');
    cartState = await r.json();
    return cartState;
  }

  async function addToCart(variantId, quantity = 1) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    if (!r.ok) throw new Error('Add to cart failed');
    await fetchCart();
    renderCart();
    openCart();
    dispatch('glose:cart:update', { cart: cartState });
  }

  async function removeCartItem(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    await fetchCart();
    renderCart();
    dispatch('glose:cart:update', { cart: cartState });
  }

  /* ── Cart render ──────────────────────────────── */
  function renderCart() {
    const itemsEl   = $('[data-cart-items]');
    const footerEl  = $('[data-cart-footer]');
    const countEl   = $('[data-cart-item-count]');
    const totalEl   = $('[data-cart-total]');
    const headerCountEls = $$('[data-cart-count]');

    if (!cartState) return;

    const count = cartState.item_count;

    // Update header count
    headerCountEls.forEach(el => {
      el.textContent = count > 0 ? `· ${count}` : '';
    });

    if (countEl) countEl.textContent = count;

    if (!itemsEl) return;

    if (count === 0) {
      itemsEl.innerHTML = `
        <div class="g-cart-empty">
          <div class="g-cart-empty__title">Bag is empty.</div>
          <div class="g-cart-empty__sub g-mono">Nothing here yet</div>
        </div>`;
      if (footerEl) footerEl.style.display = 'none';
      return;
    }

    itemsEl.innerHTML = cartState.items.map(item => `
      <div class="g-cart-drawer__item">
        ${item.featured_image?.url
          ? `<img class="g-cart-drawer__item-img" src="${item.featured_image.url}" alt="${item.title}" width="80" height="80" loading="lazy">`
          : `<div class="g-cart-drawer__item-img"></div>`}
        <div style="display:flex;flex-direction:column;gap:4px">
          <div class="g-cart-drawer__item-name">${item.product_title}</div>
          <div class="g-cart-drawer__item-variant g-mono">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title + ' · ' : ''}QTY ${item.quantity}</div>
          <div class="g-cart-drawer__item-row">
            <button class="g-cart-drawer__item-remove g-mono" data-remove-key="${item.key}">REMOVE</button>
            <span class="g-cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
          </div>
        </div>
      </div>`).join('');

    if (footerEl) {
      footerEl.style.display = 'block';
      if (totalEl) totalEl.textContent = formatMoney(cartState.total_price);
    }

    // Wire remove buttons
    $$('[data-remove-key]', itemsEl).forEach(btn => {
      btn.addEventListener('click', () => removeCartItem(btn.dataset.removeKey));
    });
  }

  /* ── Cart drawer open/close ──────────────────── */
  function openCart() {
    const drawer = $('#g-cart-drawer');
    const overlay = $('[data-cart-overlay]');
    if (!drawer) return;
    drawer.classList.add('g-cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('g-cart-overlay--visible');
    document.body.style.overflow = 'hidden';
  }

  function closeCart() {
    const drawer = $('#g-cart-drawer');
    const overlay = $('[data-cart-overlay]');
    if (!drawer) return;
    drawer.classList.remove('g-cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('g-cart-overlay--visible');
    document.body.style.overflow = '';
  }

  /* ── PDP media: lightbox + inline videos ─────────── */
  function initPDPMedia() {
    const lightbox        = $('[data-pdp-lightbox]');
    const lightboxContent = $('[data-pdp-lightbox-content]');
    const lightboxClose   = $('[data-pdp-lightbox-close]');
    if (!lightbox || !lightboxContent) return;

    let lastFocus = null;

    function openLightbox(type, src, poster) {
      lastFocus = document.activeElement;
      lightboxContent.innerHTML = '';
      if (type === 'video') {
        const v = document.createElement('video');
        v.src = src;
        if (poster) v.poster = poster;
        // Zvuk vypnutý napevno — zdrojové video má nechtěnou stopu.
        // Až se nahradí tichým souborem: muted → false, controls → true.
        v.muted = true;
        v.controls = false;
        v.loop = true;
        v.autoplay = true;
        v.playsInline = true;
        v.preload = 'auto';
        lightboxContent.appendChild(v);
      } else {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        lightboxContent.appendChild(img);
      }
      lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      lightboxClose?.focus();
    }

    function closeLightbox() {
      lightbox.hidden = true;
      lightboxContent.innerHTML = '';
      document.body.style.overflow = '';
      lastFocus?.focus?.();
    }

    // Open on media click
    $$('[data-pdp-media]').forEach(el => {
      el.addEventListener('click', () => {
        const type = el.dataset.mediaType || 'image';
        const src  = el.dataset.fullSrc;
        const poster = el.dataset.poster || '';
        if (!src) return;
        openLightbox(type, src, poster);
      });
    });

    // Close: × button, click outside content (ESC is bound once, globally)
    lightboxClose?.addEventListener('click', closeLightbox);
    lightbox.addEventListener('click', e => {
      if (e.target === lightbox) closeLightbox();
    });

    // Inline videos: autoplay only when visible (saves battery + bandwidth)
    const inlineVideos = $$('[data-pdp-inline-video]');
    if (inlineVideos.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const v = entry.target;
          if (entry.isIntersecting) {
            v.play?.().catch(() => {});
          } else {
            v.pause?.();
          }
        });
      }, { threshold: 0.25 });
      inlineVideos.forEach(v => io.observe(v));
    } else {
      inlineVideos.forEach(v => v.play?.().catch(() => {}));
    }
  }

  /* ── PDP form ────────────────────────────────── */
  function initPDP() {
    const form = $('[data-pdp-form]');
    if (!form) return;

    const variantInput  = $('[data-variant-id]', form);
    const priceEl       = $('[data-pdp-price]');
    const atcPrice      = $('[data-pdp-atc-price]');
    const atcLabel      = $('[data-pdp-atc-label]');
    const atcBtn        = $('[data-pdp-atc]');
    const qtyValEl      = $('[data-qty-val]');
    const qtyInput      = $('[data-qty-input]');
    const mainImg       = $('#g-pdp-main-img');

    // Shopify variants JSON embedded in page
    const variantsJson = document.getElementById('g-variants-json');
    let variants = [];
    if (variantsJson) {
      try { variants = JSON.parse(variantsJson.textContent); } catch(e) {}
    }

    // Option state
    const optionState = {};

    function findVariant() {
      if (!variants.length) return null;
      return variants.find(v =>
        v.options.every((val, i) => optionState[i] === val)
      ) || null;
    }

    function updateUI(variant) {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      const price = formatMoney(variant.price);
      if (priceEl) priceEl.textContent = price;
      /* Psát do spanů, ne do atcBtn.textContent — to by je smazalo. */
      if (atcPrice) atcPrice.textContent = variant.available ? ` · ${price}` : '';
      if (atcLabel) atcLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
      if (atcBtn) atcBtn.disabled = !variant.available;
      // Swap image if variant has one
      if (mainImg && variant.featured_image?.src) {
        mainImg.src = variant.featured_image.src;
      }
    }

    // Option buttons
    $$('[data-option]', form).forEach(btn => {
      const optIdx = +btn.dataset.option;
      if (!(optIdx in optionState)) optionState[optIdx] = btn.dataset.value;

      btn.addEventListener('click', () => {
        optionState[optIdx] = btn.dataset.value;
        // Update active state
        $$(`[data-option="${optIdx}"]`, form).forEach(b =>
          b.classList.toggle('g-pdp__opt-btn--active', b === btn)
        );
        // Update displayed value label
        const label = $(`[data-option-value="${optIdx}"]`, form);
        if (label) label.textContent = btn.dataset.value;

        const variant = findVariant();
        if (variant) updateUI(variant);
      });
    });

    // Qty stepper
    $$('[data-qty]', form).forEach(btn => {
      btn.addEventListener('click', () => {
        const delta = +btn.dataset.qty;
        const current = parseInt(qtyValEl?.textContent || '1', 10);
        const next = Math.max(1, current + delta);
        if (qtyValEl) qtyValEl.textContent = next;
        if (qtyInput) qtyInput.value = next;
      });
    });

    // Lightbox + inline videos
    initPDPMedia();

    // ATC form intercept
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const id = variantInput?.value;
      const qty = parseInt(qtyInput?.value || '1', 10);
      if (!id) return;
      const originalLabel = atcLabel?.textContent;
      if (atcBtn) atcBtn.disabled = true;
      if (atcLabel) atcLabel.textContent = 'Adding…';
      try {
        await addToCart(id, qty);
      } catch(err) {
        console.error('ATC error:', err);
      } finally {
        if (atcBtn) atcBtn.disabled = false;
        if (atcLabel) atcLabel.textContent = originalLabel;
      }
    });
  }

  /* ── Quick-add (collection grid) ────────────── */
  function initQuickAdd() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      const id = btn.dataset.quickAdd;
      if (!id) return;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        await addToCart(id, 1);
        btn.textContent = 'Added ✓';
        setTimeout(() => { btn.textContent = '+ Quick add'; btn.disabled = false; }, 1500);
      } catch(err) {
        btn.textContent = '+ Quick add';
        btn.disabled = false;
      }
    });
  }

  /* ── Cart button in header ───────────────────── */
  function initCartButton() {
    document.addEventListener('click', e => {
      if (e.target.closest('.g-header__cart-btn')) openCart();
      if (e.target.closest('[data-cart-close]'))   closeCart();
      if (e.target.closest('[data-cart-overlay]')) closeCart();
    });

    document.addEventListener('glose:cart:open', openCart);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeCart(); });
  }

  /* ── Countdown ───────────────────────────────── */
  function initCountdowns() {
    $$('[data-drop-date]').forEach(el => {
      if (el.dataset.gCountdown === 'on') return; // already wired
      const target = new Date(el.dataset.dropDate).getTime();
      if (isNaN(target)) return;
      el.dataset.gCountdown = 'on';

      let interval = null;
      const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
      const pad = n => String(n).padStart(2, '0');
      const set = (unit, val) => {
        const span = el.querySelector(`[data-unit="${unit}"]`);
        if (span) span.textContent = pad(val);
      };

      function tick() {
        // Hide when expired — never remove(), or a later drop date can't bring it back
        const diff = Math.max(0, target - Date.now());
        el.classList.toggle('g-countdown--expired', diff === 0);
        set('d', Math.floor(diff / 86400000));
        set('h', Math.floor((diff % 86400000) / 3600000));
        set('m', Math.floor((diff % 3600000) / 60000));
        set('s', Math.floor((diff % 60000) / 1000));
        if (diff === 0) stop();
      }

      tick();
      if (interval === null && !el.classList.contains('g-countdown--expired')) {
        interval = setInterval(tick, 1000);
        // Clean up if the section is re-rendered out from under us
        new MutationObserver((_, obs) => {
          if (!document.contains(el)) { stop(); obs.disconnect(); }
        }).observe(document.body, { childList: true, subtree: true });
      }
    });
  }

  /* ── Lookbook lightbox ───────────────────────── */
  function initLookbookLightbox() {
    const lightbox = $('[data-lookbook-lightbox]');
    if (!lightbox) return;
    const img      = $('[data-lookbook-lightbox-img]', lightbox);
    const caption  = $('[data-lookbook-lightbox-caption]', lightbox);
    const closeBtn = $('[data-lookbook-lightbox-close]', lightbox);
    let lastFocus = null;

    function close() {
      lightbox.hidden = true;
      img.src = '';
      document.body.style.overflow = '';
      lastFocus?.focus?.();
    }

    $$('[data-lookbook-zoom]').forEach(btn => {
      btn.addEventListener('click', () => {
        lastFocus = btn;
        img.src = btn.dataset.fullSrc || '';
        img.alt = btn.dataset.caption || '';
        if (caption) caption.textContent = btn.dataset.caption || '';
        lightbox.hidden = false;
        document.body.style.overflow = 'hidden';
        closeBtn?.focus();
      });
    });

    closeBtn?.addEventListener('click', close);
    lightbox.addEventListener('click', e => { if (e.target === lightbox) close(); });
  }

  /* ── Lookbook parallax ───────────────────────── */
  let parallaxRunning = false;
  function initLookbookParallax() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (parallaxRunning) return; // exactly one rAF loop, however often we re-init
    if (!document.querySelector('[data-lookbook]')) return;
    parallaxRunning = true;

    function tick() {
      // Re-query each frame so the loop survives a theme-editor section re-render
      const grid = document.querySelector('[data-lookbook]');
      if (!grid) { parallaxRunning = false; return; }
      const rect = grid.getBoundingClientRect();
      const offset = (window.innerHeight / 2 - (rect.top + rect.height / 2)) / window.innerHeight;
      const strength = (parseFloat(grid.dataset.parallaxStrength) || 100) / 100;
      grid.querySelectorAll('[data-parallax-depth]').forEach(tile => {
        const bg = tile.querySelector('.g-lookbook__tile-bg');
        if (!bg) return;
        const depth = parseFloat(tile.dataset.parallaxDepth) || 0;
        // The bg only overhangs the tile by a fixed share of its height, so cap
        // the drift there — otherwise a strong setting slides a short tile's
        // image off its own edge and the flat tile colour shows through.
        const limit = tile.offsetHeight * 0.4;
        const shift = Math.max(-limit, Math.min(limit, offset * depth * 3 * strength));
        bg.style.transform = `translate3d(0,${shift.toFixed(2)}px,0)`;
      });
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ── Init ────────────────────────────────────── */

  // Element-scoped wiring — safe to re-run against freshly rendered markup.
  function initSections() {
    initPDP();          // also wires the lightbox + inline videos
    initCountdowns();
    initLookbookLightbox();
    initLookbookParallax();
  }

  document.addEventListener('DOMContentLoaded', () => {
    fetchCart().then(() => renderCart());

    // Document-delegated listeners — must bind exactly once for the page lifetime
    initCartButton();
    initQuickAdd();
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      const pdpLightbox = $('[data-pdp-lightbox]');
      if (pdpLightbox && !pdpLightbox.hidden) $('[data-pdp-lightbox-close]')?.click();
      const lbLightbox = $('[data-lookbook-lightbox]');
      if (lbLightbox && !lbLightbox.hidden) $('[data-lookbook-lightbox-close]')?.click();
    });

    initSections();
  });

  // The theme editor swaps a section's markup in place without reloading the page.
  // Without this the fresh DOM has no handlers, so the countdown, ATC and parallax
  // look dead until the editor is refreshed by hand.
  document.addEventListener('shopify:section:load', initSections);

})();
